import { Injectable, inject } from '@angular/core';
import { AuthApiService } from '../api/auth-api.service';
import { CLAIMS } from '../../constants/auth-constants';
import { DeviceService } from '../device.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { Menu, MenuItem } from 'src/app/model/menu/menu';

@Injectable({
  providedIn: 'root',
})
export class MenuBuilderService {
  private _authApi = inject(AuthApiService);
  protected _deviceService = inject(DeviceService);
  protected _translateService = inject(TranslateService);
  private _isBuilding = false;

  menu: Menu = [];

  async buildMenu() {
    if (this._deviceService.isMobile()) {
      return await this.buildMobileMenu();
    } else {
      return await this.buildDesktopMenu();
    }
  }

  async buildMobileMenu() {
    if (this._isBuilding) {
      return this.menu;
    }

    this._isBuilding = true;
    this.menu = [];

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.MONITORING_CENTER.MON_LOGS.VIEW_MON_LOGS,
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.VIEW_MC_TURNOVERS,
      ])
    ) {
      const mc = await this.buildMobileMCMenu();
      this.menu.push(mc);
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.VIEW_CONTROLLER_NOTES
      )
    ) {
      const crm = await this.buildMobileCRMMenu();
      this.menu.push(crm);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS,
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .VIEW_COMMERCIAL_FACILITIES,
      ])
    ) {
      const commercialServices = await this.buildMobileCommercialServicesMenu();
      this.menu.push(commercialServices);
    }

    this._isBuilding = false;
    return this.menu;
  }

  async buildDesktopMenu() {
    if (this._isBuilding) {
      return this.menu;
    }

    this._isBuilding = true;
    this.menu = [];

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.MONITORING_CENTER.MON_LOGS.VIEW_MON_LOGS,
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.VIEW_MC_TURNOVERS,
      ])
    ) {
      const mc = await this.buildMCMenu();
      this.menu.push(mc);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.LOCATIONS.FACILITIES.VIEW_FACILITIES,
        CLAIMS.LOCATIONS.LOCATIONS.VIEW_LOCATIONS,
        CLAIMS.LOCATIONS.AREAS.VIEW_AREAS,
        CLAIMS.LOCATIONS.REGIONS.VIEW_REGIONS,
      ])
    ) {
      const locations = await this.buildLocationsMenu();
      this.menu.push(locations);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.CONTACTS.CONTACTS.VIEW_CONTACTS,
        CLAIMS.CONTACTS.CONTACT_LISTS.VIEW_CONTACT_LISTS,
        CLAIMS.CONTACTS.EXTERNAL_LISTS.VIEW_EXTERNAL_LISTS,
      ])
    ) {
      const contacts = await this.buildContactsMenu();
      this.menu.push(contacts);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.VIEW_CONTROLLER_NOTES,
        CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.VIEW_GC_TURNOVERS,
        CLAIMS.CONTROL_ROOM.MOC_EVENTS.VIEW_MOC_EVENTS,
      ])
    ) {
      const crm = await this.buildControlRoomMenu();
      this.menu.push(crm);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS,
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .VIEW_COMMERCIAL_FACILITIES,
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_OUTAGES.VIEW_COMMERCIAL_OUTAGES,
      ])
    ) {
      const commercialServices = await this.buildCommercialServicesMenu();
      this.menu.push(commercialServices);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.VIEW_OPERATIONAL_ALERT_LOGS,
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_ALL_ALARM_LIMITS,
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_DIRECT_REPORTS_ALARM_LIMITS,
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_OWN_ALARM_LIMITS,
        CLAIMS.FIELD_OPERATIONS.STATION_LOGS.VIEW_STATION_LOGS,
        CLAIMS.FIELD_OPERATIONS.PARTS_SEARCH.VIEW_PARTS,
        CLAIMS.FIELD_OPERATIONS.PLC_ALARM_CODES.VIEW_PLC_ALARM_CODES,
        CLAIMS.FIELD_OPERATIONS.MUSTER_LIST.VIEW_MUSTER_LIST,
      ])
    ) {
      const fieldOps = await this.buildFieldOpsMenu();
      this.menu.push(fieldOps);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.MAP.VIEW_GIS_MAP,
        CLAIMS.MAP.VIEW_FIELD_VISION,
      ])
    ) {
      const maps = await this.buildMapsMenu();
      this.menu.push(maps);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.REPORTING.VIEW_OUTAGE_REPORT,
      ])
    ) {
      const reporting = await this.buildReportingMenu();
      this.menu.push(reporting);
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.ADMINISTRATION.EMAIL_GROUPS.VIEW_EMAIL_GROUPS,
        CLAIMS.ADMINISTRATION.ROLES.VIEW_ROLES,
        CLAIMS.ADMINISTRATION.CALL_TYPES.VIEW_CALL_TYPES,
        CLAIMS.ADMINISTRATION.JOB_ROLES.VIEW_JOB_ROLES,
        CLAIMS.ADMINISTRATION.COMPANIES.VIEW_COMPANIES,
        CLAIMS.CONTROL_ROOM.MOC_EVENTS.CATEGORIES.VIEW_MOC_EVENT_CATEGORIES,
        CLAIMS.ADMINISTRATION.NOTE_TYPES.VIEW_NOTE_TYPES,
      ])
    ) {
      const admin = await this.buildAdminMenu();
      this.menu.push(admin);
    }

    this._isBuilding = false;
    return this.menu;
  }

  private async buildMCMenu(): Promise<MenuItem> {
    const mcItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        activeLogs: this._translateService.get('MONLOG.LABEL.ACTIVE_LOGS'),
        allLogs: this._translateService.get('MONLOG.LABEL.ALL_LOGS'),
        logsSearch: this._translateService.get('MONLOG.LABEL.LOGS_SEARCH'),
        shiftTurnover: this._translateService.get(
          'MONLOG.LABEL.SHIFT_TURNOVER'
        ),
        monitoringCenter: this._translateService.get(
          'MONLOG.LABEL.MONITORING_CENTER'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.MONITORING_CENTER.MON_LOGS.VIEW_MON_LOGS,
      ])
    ) {
      mcItems.push({
        label: labels.activeLogs,
        routerLink: '/monLogs/logs',
      });
      mcItems.push({
        label: labels.allLogs,
        routerLink: '/monLogs/logs/all',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.VIEW_MC_TURNOVERS,
      ])
    ) {
      mcItems.push({
        label: labels.shiftTurnover,
        routerLink: '/monLogs/turnovers',
      });
    }

    const mcBaseItem: MenuItem = {
      label: labels.monitoringCenter,
      items: mcItems,
    };

    return mcBaseItem;
  }

  private async buildMobileMCMenu(): Promise<MenuItem> {
    const mcItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        logsSearch: this._translateService.get('MONLOG.LABEL.LOGS_SEARCH'),
        monitoringCenter: this._translateService.get(
          'MONLOG.LABEL.MONITORING_CENTER'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.MONITORING_CENTER.MON_LOGS.VIEW_MON_LOGS,
      ])
    ) {
      mcItems.push({
        label: labels.logsSearch,
        routerLink: '/monLogs/logs/search',
      });
    }

    const mcBaseItem: MenuItem = {
      label: labels.monitoringCenter,
      items: mcItems,
    };

    return mcBaseItem;
  }

  private async buildLocationsMenu(): Promise<MenuItem> {
    const locationItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        facilities: this._translateService.get('LOCATION.LABEL.FACILITIES'),
        locations: this._translateService.get('LOCATION.LABEL.LOCATIONS'),
        areas: this._translateService.get('LOCATION.LABEL.AREAS'),
        regions: this._translateService.get('LOCATION.LABEL.REGIONS'),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.VIEW_FACILITIES,
      ])
    ) {
      locationItems.push({
        label: labels.facilities,
        routerLink: '/locations/facility',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.LOCATIONS.VIEW_LOCATIONS,
      ])
    ) {
      locationItems.push({
        label: labels.locations,
        routerLink: '/locations/location',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.AREAS.VIEW_AREAS,
      ])
    ) {
      locationItems.push({
        label: labels.areas,
        routerLink: '/locations/area',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.REGIONS.VIEW_REGIONS,
      ])
    ) {
      locationItems.push({
        label: labels.regions,
        routerLink: '/locations/region',
      });
    }

    const locBaseItem: MenuItem = {
      label: labels.locations,
      items: locationItems,
    };

    return locBaseItem;
  }

  private async buildContactsMenu(): Promise<MenuItem> {
    const contactsItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        profile: this._translateService.get('CONTACT.LABEL.PROFILE'),
        contact: this._translateService.get('CONTACT.LABEL.CONTACTS'),
        contactLists: this._translateService.get('CONTACT.LABEL.ON_CALL_LISTS'),
        externalLists: this._translateService.get(
          'CONTACT.LABEL.EXTERNAL_LISTS'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.CONTACTS.VIEW_CONTACTS,
      ])
    ) {
      contactsItems.push({
        label: labels.profile,
        routerLink: '/contacts/me',
      });
      contactsItems.push({
        label: labels.contact,
        routerLink: '/contacts/contact',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.CONTACT_LISTS.VIEW_CONTACT_LISTS,
      ])
    ) {
      contactsItems.push({
        label: labels.contactLists,
        routerLink: '/contacts/contactList',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.EXTERNAL_LISTS.VIEW_EXTERNAL_LISTS,
      ])
    ) {
      contactsItems.push({
        label: labels.externalLists,
        routerLink: '/contacts/contactList/external',
      });
    }

    const contactBaseItem: MenuItem = {
      label: labels.contact,
      items: contactsItems,
    };

    return contactBaseItem;
  }

  private async buildCommercialServicesMenu(): Promise<MenuItem> {
    const commercialServicesItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        commercialContacts: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_CONTACTS'
        ),
        commercialFacilities: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_FACILITIES'
        ),
        commercialOutages: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_OUTAGES'
        ),
        commercialServices: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_SERVICES'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS,
      ])
    ) {
      commercialServicesItems.push({
        label: labels.commercialContacts,
        routerLink: '/commercialServices/commercialContacts',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .VIEW_COMMERCIAL_FACILITIES,
      ])
    ) {
      commercialServicesItems.push({
        label: labels.commercialFacilities,
        routerLink: '/commercialServices/commercialFacilities',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_OUTAGES.VIEW_COMMERCIAL_OUTAGES,
      ])
    ) {
      commercialServicesItems.push({
        label: labels.commercialOutages,
        routerLink: '/commercialServices/commercialOutages',
      });
    }

    const commercialServicesBaseItem: MenuItem = {
      label: labels.commercialServices,
      items: commercialServicesItems,
    };

    return commercialServicesBaseItem;
  }

  private async buildControlRoomMenu(): Promise<MenuItem> {
    const crmItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        controllerNotes: this._translateService.get(
          'CRM.LABEL.CONTROLLER_NOTES'
        ),
        dailyOperatingPlans: this._translateService.get(
          'CRM.LABEL.DAILY_OPERATING_PLANS'
        ),
        shiftTurnovers: this._translateService.get('CRM.LABEL.SHIFT_TURNOVERS'),
        mocEvents: this._translateService.get('CRM.LABEL.MOC_EVENTS'),
        controlRoom: this._translateService.get('CRM.LABEL.CONTROL_ROOM'),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.VIEW_CONTROLLER_NOTES,
      ])
    ) {
      crmItems.push({
        label: labels.controllerNotes,
        routerLink: '/crm/controllerNotes',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.VIEW_DAILY_OPERATING_PLANS,
      ])
    ) {
      crmItems.push({
        label: labels.dailyOperatingPlans,
        routerLink: '/crm/dailyOperatingPlans',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.VIEW_GC_TURNOVERS,
      ])
    ) {
      crmItems.push({
        label: labels.shiftTurnovers,
        routerLink: '/crm/turnovers',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.MOC_EVENTS.VIEW_MOC_EVENTS,
      ])
    ) {
      crmItems.push({
        label: labels.mocEvents,
        routerLink: '/crm/mocEvents',
      });
    }

    const crmBaseItem: MenuItem = {
      label: labels.controlRoom,
      items: crmItems,
    };

    return crmBaseItem;
  }

  private async buildAdminMenu(): Promise<MenuItem> {
    let dataTablesItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        callTypes: this._translateService.get('ADMIN.LABEL.CALL_TYPES'),
        jobRoles: this._translateService.get('ADMIN.LABEL.JOB_ROLES'),
        companies: this._translateService.get('ADMIN.LABEL.COMPANIES'),
        mocEventCategories: this._translateService.get(
          'ADMIN.LABEL.MOC_EVENT_CATEGORIES'
        ),
        noteTypes: this._translateService.get('ADMIN.LABEL.NOTE_TYPES'),
        emailGroups: this._translateService.get('ADMIN.LABEL.EMAIL_GROUPS'),
        roleClaim: this._translateService.get('ADMIN.LABEL.ROLE_CLAIM_ASSIGN'),
        claimRole: this._translateService.get('ADMIN.LABEL.CLAIM_ROLE_ASSIGN'),
        roleBU: this._translateService.get(
          'ADMIN.LABEL.ROLE_BUSINESS_UNIT_ASSIGN'
        ),
        manageData: this._translateService.get(
          'ADMIN.LABEL.MANAGE_DATA_TABLES'
        ),
        administration: this._translateService.get(
          'ADMIN.LABEL.ADMINISTRATION'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.CALL_TYPES.VIEW_CALL_TYPES,
      ])
    ) {
      dataTablesItems.push({
        label: labels.callTypes,
        routerLink: '/admin/manageData/monLogTypes',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.JOB_ROLES.VIEW_JOB_ROLES,
      ])
    ) {
      dataTablesItems.push({
        label: labels.jobRoles,
        routerLink: '/admin/manageData/jobRoles',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.COMPANIES.VIEW_COMPANIES,
      ])
    ) {
      dataTablesItems.push({
        label: labels.companies,
        routerLink: '/admin/manageData/companies',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.MOC_EVENTS.CATEGORIES.VIEW_MOC_EVENT_CATEGORIES,
      ])
    ) {
      dataTablesItems.push({
        label: labels.mocEventCategories,
        routerLink: '/admin/manageData/mocEventCategories',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.NOTE_TYPES.VIEW_NOTE_TYPES,
      ])
    ) {
      dataTablesItems.push({
        label: labels.noteTypes,
        routerLink: '/admin/manageData/noteTypes',
      });
    }

    let adminItems: MenuItem[] = [];

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.EMAIL_GROUPS.VIEW_EMAIL_GROUPS,
      ])
    ) {
      adminItems.push({
        label: labels.emailGroups,
        routerLink: '/admin/emailGroups',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.ADMINISTRATION.ROLES.VIEW_ROLES,
      ])
    ) {
      adminItems.push({
        label: labels.roleClaim,
        routerLink: '/admin/claimAssignment',
      });
      adminItems.push({
        label: labels.claimRole,
        routerLink: '/admin/roleAssignment',
      });
      adminItems.push({
        label: labels.roleBU,
        routerLink: '/admin/businessUnitAssignment',
      });
    }

    if (dataTablesItems.length > 0) {
      adminItems.push({
        label: labels.manageData,
        items: dataTablesItems,
      });
    }

    let adminMenuItem: MenuItem = {
      label: labels.administration,
      items: adminItems,
    };

    return adminMenuItem;
  }

  private async buildMapsMenu(): Promise<MenuItem> {
    const mapItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        gis: this._translateService.get('MAP.LABEL.GIS_FIELD_ASSET'),
        fieldVision: this._translateService.get('MAP.LABEL.FIELD_VISION'),
        maps: this._translateService.get('MAP.LABEL.MAPS'),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([CLAIMS.MAP.VIEW_GIS_MAP])
    ) {
      mapItems.push({
        label: labels.gis,
        routerLink: '/maps/gisMapEmbed',
      });
    }

    if (
      this._authApi.doesUserHaveAllClaimsFromList([CLAIMS.MAP.VIEW_GIS_MAP])
    ) {
      mapItems.push({
        label: labels.fieldVision,
        routerLink: '/maps/fieldVision',
      });
    }

    const contactMapItem: MenuItem = {
      label: labels.maps,
      items: mapItems,
    };

    return contactMapItem;
  }

  private async buildFieldOpsMenu(): Promise<MenuItem> {
    const fieldOpsItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        fieldNotifications: this._translateService.get(
          'OPMONITORING.LABEL.FIELD_NOTIFICATIONS'
        ),
        notificationManagement: this._translateService.get(
          'OPMONITORING.LABEL.NOTIFICATION_MANAGEMENT'
        ),
        stationLogs: this._translateService.get(
          'OPMONITORING.LABEL.STATION_LOGS'
        ),
        partsSearch: this._translateService.get(
          'OPMONITORING.LABEL.PARTS_SEARCH'
        ),
        troubleshooting: this._translateService.get(
          'OPMONITORING.LABEL.TROUBLESHOOTING'
        ),
        musterList: this._translateService.get(
          'OPMONITORING.LABEL.MUSTER_LIST'
        ),
        fieldOperations: this._translateService.get(
          'OPMONITORING.LABEL.FIELD_OPERATIONS'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.VIEW_OPERATIONAL_ALERT_LOGS
      )
    ) {
      fieldOpsItems.push({
        label: labels.fieldNotifications,
        routerLink: '/opLogs/logs/all',
      });
    }

    if (
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_ALL_ALARM_LIMITS,
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_DIRECT_REPORTS_ALARM_LIMITS,
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.VIEW_OWN_ALARM_LIMITS,
      ])
    ) {
      fieldOpsItems.push({
        label: labels.notificationManagement,
        routerLink: '/operational-monitoring/alarm-limits',
      });
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.STATION_LOGS.VIEW_STATION_LOGS
      )
    ) {
      fieldOpsItems.push({
        label: labels.stationLogs,
        routerLink: '/operational-monitoring/station-logs',
      });
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.PARTS_SEARCH.VIEW_PARTS
      )
    ) {
      fieldOpsItems.push({
        label: labels.partsSearch,
        routerLink: '/operational-monitoring/partsSearch',
      });
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.PLC_ALARM_CODES.VIEW_PLC_ALARM_CODES
      )
    ) {
      fieldOpsItems.push({
        label: labels.troubleshooting,
        routerLink: '/operational-monitoring/troubleshooting',
      });
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.MUSTER_LIST.VIEW_MUSTER_LIST
      )
    ) {
      fieldOpsItems.push({
        label: labels.musterList,
        routerLink: '/operational-monitoring/muster-list',
      });
    }

    const fieldOpsBaseItem: MenuItem = {
      label: labels.fieldOperations,
      items: fieldOpsItems,
    };

    return fieldOpsBaseItem;
  }

  private async buildReportingMenu(): Promise<MenuItem> {
    const reportingItems: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        sapOutageReport: this._translateService.get(
          'REPORTING.LABEL.SAP_OUTAGE_REPORT'
        ),
        reporting: this._translateService.get('REPORTING.LABEL.REPORTING'),
      })
    );

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.REPORTING.VIEW_OUTAGE_REPORT,
      ])
    ) {
      reportingItems.push({
        label: labels.sapOutageReport,
        routerLink: '/reporting/sapOutage',
      });
    }

    const reportingItem: MenuItem = {
      label: labels.reporting,
      items: reportingItems,
    };

    return reportingItem;
  }

  private async buildMobileCRMMenu(): Promise<MenuItem> {
    const items: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        controllerNotesSearch: this._translateService.get(
          'CRM.LABEL.CONTROLLER_NOTES_SEARCH'
        ),
        controlRoom: this._translateService.get('CRM.LABEL.CONTROL_ROOM'),
      })
    );

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.VIEW_CONTROLLER_NOTES
      )
    ) {
      items.push({
        label: labels.controllerNotesSearch,
        routerLink: '/controlRoom/controllerNotes/search',
      });
    }

    const baseItem: MenuItem = {
      label: labels.controlRoom,
      items: items,
    };

    return baseItem;
  }

  private async buildMobileCommercialServicesMenu(): Promise<MenuItem> {
    const items: MenuItem[] = [];
    const labels = await firstValueFrom(
      forkJoin({
        commercialContacts: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_CONTACTS'
        ),
        commercialFacilities: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_FACILITIES'
        ),
        commercialServices: this._translateService.get(
          'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_SERVICES'
        ),
      })
    );

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS
      )
    ) {
      items.push({
        label: labels.commercialContacts,
        routerLink: '/commercialServices/commercialContacts',
      });
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .VIEW_COMMERCIAL_FACILITIES
      )
    ) {
      items.push({
        label: labels.commercialFacilities,
        routerLink: '/commercialServices/commercialFacilities',
      });
    }

    return {
      label: labels.commercialServices,
      items,
    };
  }
}
